import { useState } from 'react'
import { useUrls } from '../../../hooks/useUrls'
import { UrlItem } from '../../../types'
import { useAuth } from '../../../hooks/useAuth'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const formatDate = (value: string) => {
  try {
    return new Intl.DateTimeFormat('en', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(new Date(value))
  } catch {
    return value
  }
}

function EmptyState() {
  return (
    <div style={{ padding: '32px', textAlign: 'center', color: '#4f5b66' }}>
      <p>No saved links yet. Use the capture form above or your browser share sheet.</p>
    </div>
  )
}

function SavedItemsPanel() {
  const [pageSize, setPageSize] = useState(10)
  const [continuationToken, setContinuationToken] = useState<string | undefined>(undefined)
  const [tokenHistory, setTokenHistory] = useState<Array<string | undefined>>([])

  const { isAuthenticated, isReady, signInWithGoogle, isAuthenticating, requiresConfiguration } = useAuth()

  const { data, isLoading, isError, error, refetch, isFetching } = useUrls({
    pageSize,
    continuationToken,
  }, { enabled: isAuthenticated })

  const items: UrlItem[] = data?.Items ?? []
  const hasNextPage = Boolean(data?.ContinuationToken)
  const hasPreviousPage = tokenHistory.length > 0

  const handleNextPage = () => {
    if (data?.ContinuationToken) {
      setTokenHistory([...tokenHistory, continuationToken])
      setContinuationToken(data.ContinuationToken)
    }
  }

  const handlePreviousPage = () => {
    if (tokenHistory.length > 0) {
      const newHistory = [...tokenHistory]
      const previousToken = newHistory.pop()
      setTokenHistory(newHistory)
      setContinuationToken(previousToken)
    }
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setContinuationToken(undefined)
    setTokenHistory([])
  }

  return (
    <section style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 12px 24px -20px rgba(11,31,51,0.5)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '4px' }}>Saved items</h2>
          <p style={{ color: '#4f5b66' }}>Your saved URLs with pagination.</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="page-size" style={{ fontSize: '0.9rem', color: '#4f5b66' }}>
              Items per page:
            </label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              disabled={!isAuthenticated}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid #d0d7de',
                background: isAuthenticated ? 'white' : '#e2e8f0',
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching || !isAuthenticated}
            style={{
              padding: '8px 14px',
              border: '1px solid #d0d7de',
              borderRadius: '6px',
              background: isFetching || !isAuthenticated ? '#f3f4f6' : 'white',
              cursor: isFetching || !isAuthenticated ? 'not-allowed' : 'pointer',
            }}
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      {!isReady ? (
        <div style={{ padding: '24px', color: '#4f5b66' }}>Initialising authentication…</div>
      ) : null}

      {isReady && !isAuthenticated ? (
        <div style={{ padding: '24px', background: '#f1f5f9', borderRadius: '8px', color: '#334155', display: 'grid', gap: '12px' }}>
          <div>
            <p style={{ fontWeight: 600 }}>Sign in to view your queue</p>
            <p style={{ fontSize: '0.95rem' }}>
              Authenticate to load summaries from your connected backend.
            </p>
          </div>
          <button
            type="button"
            onClick={signInWithGoogle}
            disabled={isAuthenticating}
            style={{
              justifySelf: 'start',
              padding: '10px 18px',
              borderRadius: '8px',
              border: 'none',
              background: requiresConfiguration ? '#0b1f33' : '#0ea5e9',
              color: '#f8fafc',
              fontWeight: 600,
              cursor: isAuthenticating ? 'not-allowed' : 'pointer',
            }}
          >
            {isAuthenticating ? 'Connecting…' : requiresConfiguration ? 'Use demo account' : 'Sign in'}
          </button>
        </div>
      ) : null}

      {isLoading ? (
        <div style={{ padding: '24px', color: '#4f5b66' }}>Loading…</div>
      ) : null}

      {isError ? (
        <div style={{ padding: '24px', color: '#c0353b', background: '#fde8e8', borderRadius: '8px' }}>
          <p style={{ fontWeight: 600 }}>Unable to load saved items.</p>
          <p style={{ marginTop: '8px' }}>
            {error instanceof Error ? error.message : 'Verify the backend API is running and reachable via VITE_API_BASE_URL.'}
          </p>
        </div>
      ) : null}

      {!isLoading && !isError && isAuthenticated && items.length === 0 ? <EmptyState /> : null}

      {!isLoading && !isError && isAuthenticated && items.length > 0 ? (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#4f5b66' }}>
                  <th style={{ padding: '12px 16px' }}>Title</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Saved at</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <a
                        href={item.Url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          fontWeight: 600,
                          color: '#0b1f33',
                          textDecoration: 'none',
                          display: 'block',
                          transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#1e3a5f'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#0b1f33'}
                      >
                        {item.Title ?? '(No title)'}
                      </a>
                    </td>
                    <td style={{ padding: '14px 16px', color: '#4f5b66', fontSize: '0.9rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {formatDate(item.SavedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '12px 0', borderTop: '1px solid #e5e7eb', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ color: '#4f5b66', fontSize: '0.9rem' }}>
              Showing {items.length} item{items.length !== 1 ? 's' : ''}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handlePreviousPage}
                disabled={!hasPreviousPage || isFetching}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d0d7de',
                  borderRadius: '6px',
                  background: !hasPreviousPage || isFetching ? '#f3f4f6' : 'white',
                  cursor: !hasPreviousPage || isFetching ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={handleNextPage}
                disabled={!hasNextPage || isFetching}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d0d7de',
                  borderRadius: '6px',
                  background: !hasNextPage || isFetching ? '#f3f4f6' : 'white',
                  cursor: !hasNextPage || isFetching ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : null}
    </section>
  )
}

export default SavedItemsPanel
